import {
  ChevronDown,
  IdCard,
  PlaneLanding,
  PlaneTakeoff,
  Plus
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useWatch } from "react-hook-form";

import {
  CheckboxField,
  ComboboxField,
  CountryField,
  DateField,
  FormRow,
  FormSheet,
  SelectField,
  TextareaField,
  TextField
} from "@/components/form";
import { Button } from "@/components/ui/button";

import { ForeignNationalDocumentsAPI } from "../../../../api/foreignNationalDocumentsApi";
import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { FieldSource } from "../../../../api/types/foreignNational";
import type { ForeignNationalDocument } from "../../../../api/types/foreignNationalDocument";
import type { ForeignNationalPassport } from "../../../../api/types/foreignNationalRecords";
import type {
  ForeignNationalImmigrationEntryExitHistoryEntry,
  ForeignNationalImmigrationStatusHistoryEntry,
  ImmigrationEntryExitEventType
} from "../../../../api/types/foreignNationalImmigrationHistory";
import { IMMIGRATION_STATUS_OPTION_GROUPS } from "../../../../constants/immigrationStatusOptions";
import { useForeignNationals } from "../../../foreignNationals/context/ForeignNationalsContext";
import { SourceCell } from "../../components/SourceTag";
import {
  ClientTable,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import {
  ClientTableToolbar,
  ToolbarFilterPill
} from "../../components/table/ClientTableToolbar";
import { KitMenu } from "../../components/table/KitMenu";
import {
  BadgeCell,
  CheckCell,
  MutedCell,
  SkeletonRow,
  TitleCell
} from "../../components/table/cells";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import {
  dateInputToIso,
  normalizeText,
  toDateInputValue
} from "../../lib/format";
import { NAV_ITEMS } from "../../navConfig";
import { ImmigrationOverview } from "./ImmigrationOverview";
import { buildTimelineView } from "./timelineModel";
import {
  dateRangeLabel,
  formatDayLabel,
  mostRecentCurrent
} from "../../lib/records";
import {
  buildTimeline,
  emptyStatusDraft,
  emptyTravelDraft,
  sortStatusEntries,
  sortTravelEntries,
  statusDraftSchema,
  statusDraftToPayload,
  statusEntryToDraft,
  statusFocusDomId,
  travelDraftSchema,
  travelDraftToPayload,
  travelEntryToDraft,
  travelFocusDomId,
  type StatusDraft,
  type StatusPayload,
  type TimelineFilter,
  type TravelDraft,
  type TravelPayload
} from "./immigrationModel";

const STATUS_GROUPS = IMMIGRATION_STATUS_OPTION_GROUPS.map((g) => ({
  label: g.label,
  options: g.options.map((o) => ({ value: o.value, label: o.label ?? o.value }))
}));

const statusId = (e: ForeignNationalImmigrationStatusHistoryEntry) =>
  e.immigration_status_history_id;
const travelId = (e: ForeignNationalImmigrationEntryExitHistoryEntry) =>
  e.immigration_entry_exit_history_id;

const COLUMNS: ClientTableColumn[] = [
  { id: "type", header: "Type", className: "w-35" },
  { id: "detail", header: "Detail" },
  { id: "dates", header: "Dates" },
  { id: "current", header: "Current", className: "w-30" },
  { id: "source", header: "Source", className: "w-30" }
];

const FILTER_OPTIONS: Array<{ value: TimelineFilter; label: string }> = [
  { value: "statuses", label: "Statuses" },
  { value: "travel", label: "Travel" }
];

type Row = {
  id: string;
  domId: string;
  kind: "status" | "entry" | "exit";
  title: string;
  subtitle: string;
  dates: string;
  isCurrent: boolean;
  source?: FieldSource | null;
  haystack: string;
};

const ROW_META: Record<
  Row["kind"],
  {
    icon: typeof IdCard;
    label: string;
    variant: "brand" | "success" | "neutral";
    iconClassName?: string;
  }
> = {
  status: { icon: IdCard, label: "Status", variant: "brand" },
  entry: {
    icon: PlaneLanding,
    label: "Entry",
    variant: "success",
    iconClassName: "bg-success-soft text-success"
  },
  exit: { icon: PlaneTakeoff, label: "Exit", variant: "neutral" }
};

/**
 * Immigration timeline: status periods and entry/exit events on one table,
 * each with its own Sheet editor. Marking a status current syncs the
 * client's `current_immigration_status` / `immigration_status_expiration`.
 */
export const ImmigrationTab: FC = () => {
  const { clientId, url } = useClientProfile();
  const { updateForeignNational } = useForeignNationals();
  const [filter, setFilter] = useState<TimelineFilter>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setFilter("all");
    setSearch("");
  }, [clientId]);

  const syncCurrentStatus = useCallback(
    async (statuses: ForeignNationalImmigrationStatusHistoryEntry[]) => {
      const current = mostRecentCurrent(
        statuses,
        (e) => e.validity_start_date,
        (e) => Boolean(e.is_current)
      );
      try {
        await updateForeignNational(clientId, {
          current_immigration_status: normalizeText(current?.status) || null,
          immigration_status_expiration: current?.duration_of_status
            ? null
            : dateInputToIso(
                toDateInputValue(current?.validity_expiration_date)
              ),
          duration_of_status: Boolean(current?.duration_of_status)
        });
      } catch {
        // The context already toasts; the history save itself succeeded.
      }
    },
    [clientId, updateForeignNational]
  );

  const statuses = useRecordCrud<
    ForeignNationalImmigrationStatusHistoryEntry,
    StatusDraft,
    StatusPayload
  >({
    clientId,
    noun: "Status",
    list: ForeignNationalsAPI.listImmigrationStatusHistoryEntries,
    create: ForeignNationalsAPI.createImmigrationStatusHistoryEntry,
    update: ForeignNationalsAPI.updateImmigrationStatusHistoryEntry,
    remove: ForeignNationalsAPI.deleteImmigrationStatusHistoryEntry,
    getId: statusId,
    toDraft: statusEntryToDraft,
    emptyDraft: emptyStatusDraft,
    toPayload: statusDraftToPayload,
    schema: statusDraftSchema,
    sort: sortStatusEntries,
    afterMutate: syncCurrentStatus
  });

  const travel = useRecordCrud<
    ForeignNationalImmigrationEntryExitHistoryEntry,
    TravelDraft,
    TravelPayload
  >({
    clientId,
    noun: "Travel event",
    list: ForeignNationalsAPI.listImmigrationEntryExitHistoryEntries,
    create: ForeignNationalsAPI.createImmigrationEntryExitHistoryEntry,
    update: ForeignNationalsAPI.updateImmigrationEntryExitHistoryEntry,
    remove: ForeignNationalsAPI.deleteImmigrationEntryExitHistoryEntry,
    getId: travelId,
    toDraft: travelEntryToDraft,
    emptyDraft: emptyTravelDraft,
    toPayload: travelDraftToPayload,
    schema: travelDraftSchema,
    sort: sortTravelEntries
  });

  const [passports, setPassports] = useState<ForeignNationalPassport[]>([]);
  useEffect(() => {
    let live = true;
    ForeignNationalsAPI.listPassports(clientId)
      .then((rows) => live && setPassports(rows))
      .catch(() => live && setPassports([]));
    return () => {
      live = false;
    };
  }, [clientId]);

  const isLoading = statuses.isLoading || travel.isLoading;
  const error = statuses.error ?? travel.error;
  const timeline = useMemo(
    () => buildTimeline(statuses.records, travel.records, filter),
    [filter, statuses.records, travel.records]
  );
  const total = statuses.records.length + travel.records.length;
  const overview = useMemo(
    () => buildTimelineView(statuses.records, travel.records, passports),
    [passports, statuses.records, travel.records]
  );

  const { focus } = url.state;
  const focusDomId = focus.immigrationStatusHistoryId
    ? statusFocusDomId(focus.immigrationStatusHistoryId)
    : focus.immigrationEntryExitHistoryId
    ? travelFocusDomId(focus.immigrationEntryExitHistoryId)
    : null;
  const { highlightedId } = useFocusScroll(focusDomId, !isLoading && total > 0);

  const rows = useMemo<Row[]>(() => {
    const all = timeline.map((item): Row => {
      if (item.kind === "status") {
        const e = item.entry;
        const title = normalizeText(e.status) || "Status";
        const subtitle =
          [
            e.approval_date
              ? `Approved ${formatDayLabel(e.approval_date)}`
              : e.filing_date
              ? `Filed ${formatDayLabel(e.filing_date)}`
              : "",
            normalizeText(e.receipt_number),
            normalizeText(e.employer)
          ]
            .filter(Boolean)
            .join(" · ") || "Immigration status";
        const dates = e.duration_of_status
          ? `${
              dateRangeLabel(e.validity_start_date, null, false).split(" – ")[0]
            } – D/S`
          : dateRangeLabel(
              e.validity_start_date,
              e.validity_expiration_date,
              e.is_current && !e.validity_expiration_date
            );
        return {
          id: `status:${item.id}`,
          domId: statusFocusDomId(item.id),
          kind: "status",
          title,
          subtitle,
          dates,
          isCurrent: Boolean(e.is_current),
          source: e.source,
          haystack: `${title} ${subtitle} ${dates} ${e.notes ?? ""}`
        };
      }
      const e = item.entry;
      const isEntry = e.event_type === "ENTRY";
      const place = normalizeText(e.port_of_entry) || normalizeText(e.country);
      const title = `${isEntry ? "Entered" : "Departed"}${
        place ? ` · ${place}` : ""
      }`;
      const subtitle =
        [
          normalizeText(e.class_of_admission),
          normalizeText(e.i94_record_number)
            ? `I-94 ${e.i94_record_number}`
            : ""
        ]
          .filter(Boolean)
          .join(" · ") || (isEntry ? "Entry record" : "Exit record");
      const dates = formatDayLabel(e.event_date);
      return {
        id: `travel:${item.id}`,
        domId: travelFocusDomId(item.id),
        kind: isEntry ? "entry" : "exit",
        title,
        subtitle,
        dates,
        isCurrent: false,
        source: e.source,
        haystack: `${title} ${subtitle} ${dates} ${e.country ?? ""} ${
          e.notes ?? ""
        }`
      };
    });
    const q = search.trim().toLowerCase();
    return q ? all.filter((r) => r.haystack.toLowerCase().includes(q)) : all;
  }, [search, timeline]);

  const openItem = (compositeId: string) => {
    const [kind, id] = compositeId.split(":", 2);
    if (kind === "status") {
      const e = statuses.records.find((s) => statusId(s) === id);
      if (e) {
        travel.close();
        statuses.openEdit(e);
      }
    } else {
      const e = travel.records.find((t) => travelId(t) === id);
      if (e) {
        statuses.close();
        travel.openEdit(e);
      }
    }
  };

  const addStatus = () => {
    travel.close();
    statuses.openCreate();
  };
  const addTravel = (eventType: ImmigrationEntryExitEventType) => {
    statuses.close();
    travel.openCreate({ eventType });
  };

  const refreshAll = () => {
    statuses.refresh().catch(() => undefined);
    travel.refresh().catch(() => undefined);
  };

  const empty =
    total === 0
      ? {
          title: "Build Your Client's Immigration Timeline",
          icon: IdCard,
          message:
            "Track status changes and travel history so their immigration record is always accurate.",
          cta: "Add Status",
          onCta: addStatus
        }
      : filter === "statuses" && !search.trim()
      ? {
          title: "No Statuses Yet",
          icon: IdCard,
          cta: "Add Status",
          onCta: addStatus
        }
      : filter === "travel" && !search.trim()
      ? {
          title: "No Travel Events Yet",
          icon: PlaneLanding,
          cta: "Add Entry Record",
          onCta: () => addTravel("ENTRY")
        }
      : {
          title: "No Matching Events",
          icon: IdCard,
          message: "Try a different search."
        };

  const travelEventType = useWatch({
    control: travel.form.control,
    name: "eventType"
  });
  const isEntry = travelEventType !== "EXIT";
  const durationOfStatus = useWatch({
    control: statuses.form.control,
    name: "durationOfStatus"
  });
  const [documents, setDocuments] = useState<ForeignNationalDocument[]>([]);
  const statusEditorOpen = statuses.editor.mode !== "closed";
  useEffect(() => {
    if (!statusEditorOpen || !clientId) return undefined;
    let live = true;
    ForeignNationalDocumentsAPI.list(clientId)
      .then((docs) => live && setDocuments(docs))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [clientId, statusEditorOpen]);
  const evidenceOptions = useMemo(
    () =>
      documents.map((d) => ({
        value: d.foreign_national_document_id,
        label: d.label ? `${d.label} · ${d.filename}` : d.filename
      })),
    [documents]
  );

  useEffect(() => {
    if (durationOfStatus && statuses.form.getValues("validityExpirationDate")) {
      statuses.form.setValue("validityExpirationDate", "", {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [durationOfStatus, statuses.form]);
  const statusOpen = statuses.editor.mode !== "closed";
  const travelOpen = travel.editor.mode !== "closed";

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.immigration_overview.label}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search immigration history...",
          ariaLabel: "Search immigration history"
        }}
        filters={
          <ToolbarFilterPill<TimelineFilter>
            appearance="ghost"
            icon="FeatherFilter"
            label="Show"
            ariaLabel="Filter timeline"
            value={filter}
            options={FILTER_OPTIONS}
            allOption={{ value: "all", label: "All Events" }}
            onChange={setFilter}
          />
        }
        onRefresh={refreshAll}
        isRefreshing={isLoading}
        primaryAction={
          <KitMenu
            align="end"
            trigger={
              <Button variant="primary">
                <Plus />
                Add
                <ChevronDown />
              </Button>
            }
            items={[
              {
                key: "status",
                label: "Immigration Status",
                icon: "FeatherCreditCard",
                onSelect: addStatus
              },
              {
                key: "entry",
                label: "Entry Record",
                icon: "FeatherPlaneLanding",
                onSelect: () => addTravel("ENTRY")
              },
              {
                key: "exit",
                label: "Exit Record",
                icon: "FeatherPlaneTakeoff",
                onSelect: () => addTravel("EXIT")
              }
            ]}
          />
        }
      />
      {total > 0 ? (
        <div className="shrink-0 px-3 pb-3 pt-1">
          <ImmigrationOverview view={overview} />
        </div>
      ) : null}
      <ClientTable
        ariaLabel="Immigration history"
        columns={COLUMNS}
        isLoading={isLoading}
        isEmpty={rows.length === 0}
        error={error}
        onRetry={refreshAll}
        empty={empty}
        renderSkeletonRow={(i) => (
          <SkeletonRow
            key={i}
            widths={["w-16", "w-48", "w-32", "w-12", "w-16"]}
          />
        )}
      >
        {rows.map((row) => {
          const meta = ROW_META[row.kind];
          return (
            <ClientTableRow
              key={row.id}
              domId={row.domId}
              highlighted={highlightedId === row.domId}
              onActivate={() => openItem(row.id)}
            >
              <BadgeCell label={meta.label} variant={meta.variant} />
              <TitleCell
                icon={meta.icon}
                iconClassName={
                  row.isCurrent
                    ? "bg-accent-soft text-accent-seed"
                    : meta.iconClassName
                }
                title={row.title}
                subtitle={row.subtitle}
              />
              <MutedCell>{row.dates}</MutedCell>
              <CheckCell checked={row.isCurrent} />
              <SourceCell source={row.source} />
            </ClientTableRow>
          );
        })}
      </ClientTable>

      {/* Status editor */}
      <FormSheet
        form={statuses.form}
        open={statusOpen}
        onOpenChange={(open) => {
          if (!open) statuses.close();
        }}
        title={
          statuses.editor.mode === "edit"
            ? "Edit Immigration Status"
            : "Add Immigration Status"
        }
        description="A period the client held a status, with the key dates."
        onSubmit={statuses.submit}
        isEditing={statuses.editor.mode === "edit"}
        isSubmitting={statuses.isSaving}
        isDeleting={statuses.isDeleting}
        onDelete={() => {
          statuses.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this status?"
        deleteDescription="The client's current status will be recalculated from the remaining entries."
      >
        <SelectField<StatusDraft>
          name="status"
          label="Status"
          required
          groups={STATUS_GROUPS}
        />
        <FormRow>
          <DateField<StatusDraft>
            name="validityStartDate"
            label="Valid From"
            required
          />
          <DateField<StatusDraft>
            name="validityExpirationDate"
            label="Valid Until"
            disabled={durationOfStatus}
            placeholder={durationOfStatus ? "D/S" : "Pick a date"}
          />
        </FormRow>
        <CheckboxField<StatusDraft>
          name="durationOfStatus"
          label="Admitted for duration of status (D/S)"
          description="F and J admissions have no fixed end date on the I-94."
        />
        <FormRow>
          <DateField<StatusDraft> name="filingDate" label="Filing Date" />
          <DateField<StatusDraft> name="approvalDate" label="Approval Date" />
        </FormRow>
        <FormRow>
          <TextField<StatusDraft>
            name="receiptNumber"
            label="Receipt Number"
            placeholder="e.g. EAC2190012345"
            autoComplete="off"
          />
          <TextField<StatusDraft>
            name="employer"
            label="Sponsoring Employer"
            placeholder="For H-1B, L-1, O-1, TN..."
          />
        </FormRow>
        <ComboboxField<StatusDraft>
          name="evidenceDocumentId"
          label="Evidence"
          options={evidenceOptions}
          placeholder="Approval notice or I-94"
          searchPlaceholder="Search client documents…"
          emptyText="No documents in the client's file."
          description="The I-797 or I-94 that shows this status."
        />
        <CheckboxField<StatusDraft>
          name="isCurrent"
          label="This is the client's current status"
          description="Sets the client's current immigration status and expiration on their profile."
        />
        <TextareaField<StatusDraft>
          name="notes"
          label="Notes"
          placeholder="Add any additional notes about this immigration status..."
        />
      </FormSheet>

      {/* Travel editor */}
      <FormSheet
        form={travel.form}
        open={travelOpen}
        onOpenChange={(open) => {
          if (!open) travel.close();
        }}
        title={
          travel.editor.mode === "edit"
            ? isEntry
              ? "Edit Entry Record"
              : "Edit Exit Record"
            : isEntry
            ? "Add Entry Record"
            : "Add Exit Record"
        }
        description="A border crossing and the admission details recorded with it."
        onSubmit={travel.submit}
        isEditing={travel.editor.mode === "edit"}
        isSubmitting={travel.isSaving}
        isDeleting={travel.isDeleting}
        onDelete={() => {
          travel.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this travel event?"
      >
        <FormRow>
          <SelectField<TravelDraft>
            name="eventType"
            label="Event"
            options={[
              { value: "ENTRY", label: "Entry (Arrival)" },
              { value: "EXIT", label: "Exit (Departure)" }
            ]}
          />
          <DateField<TravelDraft>
            name="eventDate"
            label="Event Date"
            required
          />
        </FormRow>
        <FormRow>
          <TextField<TravelDraft>
            name="portOfEntry"
            label={isEntry ? "Port of Entry" : "Port of Exit"}
            placeholder="e.g., JFK"
          />
          <CountryField<TravelDraft> name="country" label="Country" />
        </FormRow>
        <FormRow>
          <TextField<TravelDraft>
            name="classOfAdmission"
            label="Class of Admission"
            placeholder="e.g., H-1B"
          />
          <TextField<TravelDraft>
            name="i94RecordNumber"
            label="I-94 Record Number"
            placeholder="Enter I-94 record number"
          />
        </FormRow>
        <DateField<TravelDraft>
          name="i94ExpirationDate"
          label="I-94 Expiration Date"
        />
        <TextareaField<TravelDraft>
          name="notes"
          label="Notes"
          placeholder="Add any additional notes about this entry/exit event..."
        />
      </FormSheet>
    </div>
  );
};
